// mwtRouter.js — multi-workspace-tabs router wrapper (Phase 3, §6a).
//
// Registered as `context.functions.router` so EVERY `el.router()` + `Link`/
// `RouteLink` click funnels through it. When MWT is ON it emits + consumes an
// optional `/w/<org-slug>` prefix on the workspaceProject INTRANET routes only
// (canvas / marketplace / preview keep their own bare URLs, §6b). When OFF it is
// a transparent pass-through — the app navigates exactly as today.
//
// smbls' router couples matchRoute + pushState to ONE pathname (plugins/router
// index.js:229/281), so the wrapper writes the PREFIXED URL to the address bar
// itself, then calls the default router with the STRIPPED path + `pushState:false`
// to render the real route without overwriting the URL. `mwtOnRenderRouter`
// covers the initial hard-load render, which bypasses `context.functions.router`
// (smbls/src/router.js `onRouterRenderDefault` calls the default router directly).
//
// The pure decision (planMwtNav) lives in shared/functions/mwtNav.js so it stays
// testable without `@symbo.ls/router`. This module only wires it to the router +
// history. Plain module, direct ESM import into context.js's functions map —
// FA001 legally allows context.js to import it (frank-audit's sibling-import
// allowlist covers context.js itself), but that is NOT the same claim as
// "never frank-serialized": frank evaluates context.js's REAL merged
// `functions` object at toJSON time, so this function DOES get serialized —
// correctly, since workspace-shell's own JSON needs it. The "never leaks
// into a consumer" guarantee comes from `sharedLibIgnore: ['app',
// 'functions/router']` on context.js (packages/utils/sharedLibraries.js
// `mergeOneLibrary`), not from anything here. A prior version of this
// comment claimed the opposite and was wrong — the platform push serialized
// this function into workspace-shell's library form and it hijacked every
// consumer's routing until the sharedLibIgnore fix landed. See
// tickets/opus.md "shared-library pushes serialize app-wiring functions".

import { router as defaultRouter } from '@symbo.ls/router'
import { isCanvasProjectPath } from './canvasProjectRedirect.js'
import _wsShared from '@symbo-ls/workspace-shared'
import { shellStripWorkspacePrefix } from './shellSharedDelegates.js'
import { whitelabelForeignRedirect } from './whitelabelRouteGuard.js'
const shellIsMwtEnabled = (...args) => _wsShared.functions.isMwtEnabled(...args)
const planMwtNav = (...args) => _wsShared.functions.planMwtNav(...args)
const mwtOrgToReconcile = (...args) => _wsShared.functions.mwtOrgToReconcile(...args)
const isMwtIntranetRoute = (...args) => _wsShared.functions.isMwtIntranetRoute(...args)
const shellResolveWorkspaceUrlHandle = (...args) =>
  _wsShared.functions.resolveWorkspaceUrlHandle(...args)
const moduleRoutesFromRoot = (...args) => _wsShared.functions.moduleRoutesFromRoot(...args)
const shellGetWorkspacePrefixSlug = (...args) => _wsShared.functions.getWorkspacePrefixSlug(...args)
const shellGetWorkspaceRawSegments = (...args) => _wsShared.functions.getWorkspaceRawSegments(...args)
const shellWithWorkspacePath = (...args) => _wsShared.functions.withWorkspacePath(...args)
const _mwtWin = (el) => el?.context?.window || (typeof window !== 'undefined' ? window : null)
const _orgSlug = (el) => {
  try {
    const org = el?.getRoot?.()?.state?.org
    return (org && (org.slug || org.username)) || null
  } catch {
    return null
  }
}

// The workspace handle to stamp on emitted intranet URLs (scheme A: path
// segment 2) — the shared rule in workspaceUrlSync.js (non-HQ always carries
// it; HQ never needs it; legacy untyped records fall back to a strict
// known-siblings check). This used to be a local copy of the sibling heuristic
// whose `!orgId ||` clause matched EVERY workspace while the org was still
// resolving — so any nav racing boot stamped a redundant handle onto
// single-workspace orgs.
const _workspaceHandle = (el) => {
  try {
    return shellResolveWorkspaceUrlHandle(el?.getRoot?.()?.state)
  } catch {
    return null
  }
}

const _moduleRoutes = (el) => moduleRoutesFromRoot(el?.getRoot?.()?.state)

// context.functions.router — outbound nav (el.router / Link) + smbls popstate.
export const mwtRouter = (path, el, state = {}, opts = {}) => {
  const plan = planMwtNav({
    path,
    slug: _orgSlug(el),
    ws: _workspaceHandle(el),
    moduleRoutes: _moduleRoutes(el),
    mwtEnabled: shellIsMwtEnabled()
  })
  const win = _mwtWin(el)
  // MWT OFF — pass-through for the ROUTE, but the reserved `/w/<slug>` prefix
  // is still stripped for MATCHING. Same invariant mwtOnRenderRouter states
  // and enforces for the hard load: "a prefixed URL must resolve to its real
  // route whether or not MWT is enabled" — stripWorkspacePrefix is a no-op
  // off-prefix, so an unprefixed nav is byte-identical to before.
  //
  // WS-CUSTOM-DOMAIN-HUB-SILENT-BOOT-FAIL-1: this branch used to hand the RAW
  // path to the default router, which matches a level-1 key — so an in-app
  // `el.router('/w/bellforge/')` matched the BARE `/w` route (the workspace
  // hub) instead of `/`. The hub then forwards a single-org user to
  // `withWorkspacePrefix(slug, '/')`, which is the very path it is already on,
  // so it re-mounted itself forever under "Opening your workspace…". Nothing
  // consumes the prefix with the flag off (reconcileMwtOrgFromUrl returns
  // immediately), so the URL is unhonourable and healed away rather than left
  // stranded — exactly what the hard-load twin does.
  if (!plan) {
    const matchPath = shellStripWorkspacePrefix(path)
    if (matchPath !== path && opts.pushState === false && win?.history) {
      try {
        win.history.replaceState(state, '', matchPath)
      } catch {}
    }
    return defaultRouter(matchPath, el, state, opts)
  }
  // Prefix intranet targets: write the /w/<slug> URL ourselves, then render the
  // stripped route without letting the default router overwrite the address bar.
  // Honor pushState:false (popstate / initial render) — never double-write.
  // `replace: true` REWRITES the current entry, exactly as the default router
  // does for an unprefixed URL (plugins/router `replace`): this branch used to
  // push whatever the caller asked, so a query rewrite on a workspace URL
  // stacked a Back entry for every write (functions/assistantThreadUrl.js is
  // the first caller that needs a true replace here).
  if (plan.addressBarUrl && opts.pushState !== false && win?.history) {
    try {
      if (opts.replace) win.history.replaceState(state, null, plan.addressBarUrl)
      else win.history.pushState(state, null, plan.addressBarUrl)
    } catch {}
    return defaultRouter(plan.matchPath, el, state, { ...opts, pushState: false })
  }
  return defaultRouter(plan.matchPath, el, state, opts)
}

// context.functions.routerHref — the `href` an in-app Link RENDERS (F2). The
// framework Link (smbls r17, attrs-in-props `resolveRouterHref`) hands every
// in-app `/…` href to this hook. It returns the address a plain click puts in
// the address bar — the SAME planMwtNav decision `mwtRouter` above writes — so
// a Cmd/Ctrl/Shift/middle click, "Copy link address" and the status bar name
// this tab's org and workspace instead of a bare path that boots the last-used
// one. MWT off, a non-intranet route (canvas / marketplace / preview) or no
// org yet → the href as written, byte for byte.
//
// `attr.href` runs in a TRACKED effect: the three readers run first and
// unconditionally — they read the root's `org`, `workspace`, `workspaceProject`
// and `workspaceApps` before planMwtNav can return — so a link rendered before
// boot knows the org renders again, prefixed, once it does. Reading behind an
// early return would subscribe to nothing (the chat EmojiPicker trap, 10-24).
export const mwtRouterHref = (href, el) => {
  const slug = _orgSlug(el)
  const ws = _workspaceHandle(el)
  const moduleRoutes = _moduleRoutes(el)
  const plan = planMwtNav({ path: href, slug, ws, moduleRoutes, mwtEnabled: shellIsMwtEnabled() })
  return (plan && plan.addressBarUrl) || href
}

// Scheme A late-strip heal. The initial hard-load dispatch
// (mwtOnRenderRouter) strips path segment 2 only when the handle resolver
// already recognizes it — on a COLD boot the org/workspace lists haven't
// landed yet, so `/w/<org>/<handle>/<route…>` matched `/<handle>/<route…>`
// and rendered Not-Found. Once the data lands and the resolver flips, this
// re-dispatches the canonical stripped route IN PLACE (pushState:false — the
// address bar is already correct). Self-retries while the lists are still
// loading; once they're present and the resolver still says "route", the
// segment genuinely IS a route and the current render stands.
export const _healSchemeARoute = (root, el, _try = 0) => {
  if (!el) return
  const win = _mwtWin(el)
  if (!win?.location) return
  const path = win.location.pathname || '/'
  const { seg2 } = shellGetWorkspaceRawSegments(path)
  if (!seg2) return
  const stripped = shellStripWorkspacePrefix(path)
  const stillAmbiguous = stripped.split('/')[1] === seg2
  if (!stillAmbiguous) {
    // Resolver recognized segment 2 as a workspace — re-render the real route
    // unless the initial dispatch already matched it (resolver was warm).
    if (String(root.currentPath || '') === stripped) return
    // ...and unless the router already RENDERED that route. `currentPath` is
    // seeded with the RAW pathname by app.js onCreate ('/w/<org>/<ws>'), so the
    // check above never held on a Scheme A URL, and this heal re-dispatched a
    // route that was already on screen: on a cold /w/unreal/default load the
    // whole home page was built a second time (3.9 s of synchronous work at
    // load 75, dev deploy 31). The router records what it rendered as
    // `route` / `routePath` (its first-segment key) on the root state.
    const _want = '/' + (stripped.split('/')[1] || '')
    if (root.route === _want || root.routePath === _want) return
    // ...and unless the INITIAL dispatch already asked for this exact route and
    // is still in flight (the router writes route/routePath only after its auth
    // guard resolves; this heal runs from the signin tick, ~150 ms after the
    // first dispatch on dev deploy 33 - and rebuilt the home a second time).
    if (el.__ref && el.__ref.__mwtInitialPath === stripped) return
    // Canvas-project paths (/w/<org>[/<ws>]/p/<key>) mount the editor off the
    // RAW pathname via the '/*' catch-all (projectRedirect classification) —
    // a resolver-warm re-dispatch would just remount the same editor once
    // (~1-2s into a cold load, visible re-render) for zero routing gain.
    if (isCanvasProjectPath(win.location.pathname)) return
    try {
      // Promise.resolve(...).catch: the router can also reject ASYNCHRONOUSLY
      // (mid-heal teardown, half-mounted el) — a bare try/catch only covers
      // the sync throw and left an unhandledRejection.
      Promise.resolve(
        defaultRouter(
          stripped + (win.location.search || '') + (win.location.hash || ''),
          el,
          {},
          { pushState: false }
        )
      ).catch(() => {})
    } catch {}
    return
  }
  // "Data present" = a source the resolver can actually DECIDE from: the org
  // list (needed to attribute a handle to the path's org) plus either
  // workspaces list, or the settled active workspace + org records (the
  // resolver's list-independent fast path). Until one exists, keep polling;
  // once one does and the segment still reads ambiguous, it's a genuine
  // route.
  const orgsReady = Array.isArray(root.orgs) && root.orgs.length
  const listsReady =
    orgsReady &&
    ((Array.isArray(root.workspaces) && root.workspaces.length) ||
      (root.orgWorkspaces && Object.keys(root.orgWorkspaces).length))
  const dataReady = listsReady || (root.workspace && root.org)
  if (!dataReady || (seg2.startsWith('ws_') && !listsReady)) {
    // The ghost-handle branch below needs the FULL workspace lists: the
    // fast path (active workspace + org alone) can only recognize the
    // ACTIVE workspace's own handle, so judging a SIBLING-workspace deep
    // link on it would rescope a legitimate URL away while the lists are
    // still loading.
    if (_try < 50) setTimeout(() => _healSchemeARoute(root, el, _try + 1), 200)
    return
  }
  // SHELL-WS-HANDLE-1 (tickets/bellforge.md, 2026-08-15). Data landed and
  // segment 2 STILL does not resolve. For a plain segment that is the
  // documented Scheme A outcome — unmatched segments read as routes on the
  // HQ (relman's bare-URL finding: that half of the ticket was correct
  // behaviour mislabelled as a bug). But a `ws_`-prefixed segment can NEVER
  // be a route: server-side workspace slugs are minted against a reserved
  // blacklist mirroring the static route names, and raw ids always carry
  // the `ws_` shape prefix (workspaceUrlSync.js's own contract). So an
  // unresolvable ws_ segment is a GHOST handle — deleted workspace, another
  // member's inaccessible workspace, or a typo — and letting it read as a
  // route left the HQ rendering under a URL naming a workspace that does
  // not exist for this session (same label-lie class as TENANCY-URL-1).
  // Rescope the address bar to the active workspace's canonical URL and
  // re-dispatch the root; the ghost's trailing route segments belong to the
  // ghost's namespace and are not reinterpreted.
  if (seg2.startsWith('ws_')) {
    const activeOrg = root.org
    const activeSlug = activeOrg?.slug || activeOrg?.username
    if (!activeSlug) return
    const handle = shellResolveWorkspaceUrlHandle({
      workspace: root.workspace,
      org: activeOrg,
      workspaces: Array.isArray(root.workspaces) ? root.workspaces : []
    })
    const target = shellWithWorkspacePath(activeSlug, handle, '/')
    console.warn(
      '[mwt] workspace handle "' +
        seg2 +
        '" resolves to nothing for this session — ' +
        'rescoped to ' +
        target +
        ' (a ws_-shaped segment is never a route)'
    )
    try {
      win.history.replaceState(win.history.state, '', target)
    } catch {}
    try {
      // Same async-rejection guard as the re-dispatch above.
      Promise.resolve(defaultRouter('/', el, {}, { pushState: false })).catch(() => {})
    } catch {}
  }
}

// Cross-org URL reconcile (Phase 5). Call POST-AUTH (from the shell's
// subscribeAuthSession onSignIn), when `root.org` is established. If this tab's
// URL pins an org different from the one boot resolved — a deep-link/reload of
// `/w/<other-org>/…`, whose workspaces aren't loadable pre-mount — client-only
// switch to it: `sdk.switchOrg(id, {skipPersist:true})` loads that org into THIS
// tab's root via the existing onWorkspaceChange pipeline, WITHOUT the Mongo PATCH
// (no server global mutation) or a cross-tab broadcast. Idempotent + guarded
// (no-op once active org matches the URL); empirically loop-free (the auth-tick
// does not revert a skipPersist switch). No-op when the MWT flag is off.
// `el` (any shell element) powers the scheme-A route heal — optional so older
// callers stay valid, but the shell passes it.
//
// `opts.ownerSlugForPath(path)` extends the reconcile to URL shapes that name an
// org WITHOUT the `/w/` prefix — the project canvas `/<owner>/<key>` and
// `/studio|/workspaces/<owner>`. Those are the shapes that produced a chrome
// that contradicted the address bar: landing on `/aihouse/site` left the org
// switcher and the whole navbar showing the PERSISTED org (Natali) while the
// canvas rendered an aihouse project, because this function only ever asked
// `getWorkspacePrefixSlug` — a `/w/` reader — and then fell through to the
// canonicalize branch, which returns early for anything non-intranet.
//
// It is a CALLBACK, not a slug string, for two reasons: the retries below
// re-read `window.location` (a slug captured at call time would go stale if the
// route changed mid-retry), and the "is this 2-segment path a project canvas?"
// test needs the shell's reserved-route + installed-module lists, which live at
// the call site. Deriving that here would mean a second hand-maintained copy of
// the reserved list — exactly the drift this fix exists to remove.
export const reconcileMwtOrgFromUrl = (root, sdk, el, _attempt = 0, opts = {}) => {
  if (!shellIsMwtEnabled() || !sdk || !root || typeof sdk.switchOrg !== 'function') return
  // Fire the route heal exactly once per reconcile entry (attempt 0) — it
  // owns its own retry loop, independent of the org-reconcile retries below.
  if (_attempt === 0) _healSchemeARoute(root, el)
  // The SDK's CURRENT org — what `switchOrg` keys on to load data. Populated on a
  // `/w/<slug>` deep-link (auth.js seeds the SDK context), but NOT on a bare load:
  // there resolveActiveOrgId sets `root.org` only, so the active org lives solely
  // in root. The two branches below therefore draw from different sources.
  const sdkOrgId = sdk._context?.activeOrgId
  const rootOrgId = root.org && (root.org.id ?? root.org._id)
  // Retry (~5s) until the orgs list AND some notion of the active org land —
  // either source clears the shared guard. The cross-org branch re-checks
  // `sdkOrgId` on its own (a URL-pinned `root.org` can't drive that comparison);
  // the canonicalize branch is happy with `root.org` (bare loads have no sdkOrgId,
  // so gating the shared guard on sdkOrgId starved canonicalize entirely).
  if ((!sdkOrgId && !rootOrgId) || !Array.isArray(root.orgs) || !root.orgs.length) {
    if (_attempt < 25)
      setTimeout(() => reconcileMwtOrgFromUrl(root, sdk, el, _attempt + 1, opts), 200)
    return
  }
  const win = typeof window !== 'undefined' ? window : null
  if (!win || !win.location) return
  const path = win.location.pathname || '/'
  // Any URL shape that NAMES an org feeds the same reconcile. `/w/<org>/…`
  // states it in the prefix; a project canvas states it as the owner segment;
  // `/studio|/workspaces/<owner>` states it as segment 2. Previously only the
  // first was consulted, so the other two rendered the persisted org's chrome
  // over another org's content.
  let slug = shellGetWorkspacePrefixSlug(path)
  // Which "currently active org" the URL's slug is compared against differs by
  // shape, and getting it wrong is why the first cut of this fix did nothing:
  //
  //  • `/w/<slug>` — auth.js URL-PINS `root.org` to the slug, so root would
  //    always look like it is "already there". The SDK context is the only
  //    honest comparand, and it IS populated on a MEMBER-org landing. On a
  //    FOREIGN slug nothing may ever populate it (see the ladder-spent
  //    fallback below); root.org is honest there, as the pin skips non-members.
  //  • project canvas / `/studio/<owner>` — nothing pins `root.org`; it holds
  //    the genuinely-active (persisted) org. And on such a BARE load the SDK
  //    context's `activeOrgId` is never populated at all (see the comment on
  //    sdkOrgId above), so requiring it here meant 25 retries and a silent
  //    give-up — the reconcile ran, found no SDK org, and quit every time.
  let activeForCompare = sdkOrgId
  if (!slug && typeof opts.ownerSlugForPath === 'function') {
    try {
      slug = opts.ownerSlugForPath(path) || null
    } catch (err) {
      console.warn('[mwt] ownerSlugForPath threw:', err?.message || err)
    }
    if (slug) activeForCompare = sdkOrgId || rootOrgId
  }

  if (slug) {
    // Cross-org: switch this tab to the URL's org when the active one differs
    // (loads that org's data client-only). Retry while the comparand is still
    // resolving — async on a fresh deep link.
    if (!activeForCompare) {
      if (_attempt < 25) {
        setTimeout(() => reconcileMwtOrgFromUrl(root, sdk, el, _attempt + 1, opts), 200)
        return
      }
      // Ladder spent and still no SDK org: on a FOREIGN `/w/` slug that is the
      // steady state, not a race. The boot seeds sdk._context.activeOrgId from
      // the URL org (null for a non-member) or user.activeOrganization — with
      // no resolved workspace only when that org is in the session's own list
      // (bootResolve's own-org seed) — and skips its pre-render switchOrg for a
      // non-member org, so for an unset or departed stored org no writer is
      // coming. Returning here kept the foreign address over the session's own
      // workspace for good, with no log (dev.my 2026-10-30: test user Dan on
      // /w/kestrel-assist/default). Compare against root.org instead: auth.js
      // pins it to a URL org only for a member, so a foreign slug can never
      // read as "already there", and the own-org guard below still keeps a
      // pinned member URL from bouncing. The entry gate guarantees rootOrgId.
      activeForCompare = rootOrgId
    }
    const targetOrgId = mwtOrgToReconcile({ slug, orgs: root.orgs, activeOrgId: activeForCompare })
    if (!targetOrgId) {
      // Aligned fast-exit: bootShell now pre-seeds sdk._context.activeOrgId on
      // every /w/ landing (and pre-switches on cross-org ones), so "no target"
      // usually means MATCHED, not not-yet-resolved. When the slug's org is
      // resolvable and equals the comparand there is nothing to reconcile —
      // return now instead of burning the 25×200ms retry ladder.
      const _urlOrg = root.orgs.find((o) => o && (o.slug === slug || o.username === slug))
      const _urlOrgId = _urlOrg ? String(_urlOrg.id ?? _urlOrg._id ?? '') : null
      if (_urlOrgId && String(activeForCompare) === _urlOrgId) return
      // Two reasons collapse into the same "no target" result here, and a
      // single check can't tell them apart: genuinely not a member of
      // `slug` (mwtOrgToReconcile's own guard — never attempt a switch onto
      // a stranger's public project), OR `root.orgs` — already non-empty
      // enough to pass the length-only gate above — just hasn't grown to
      // include THIS org yet (2026-08-02 "navbar changes not always saves"
      // investigation, Nika: a project-canvas route, e.g. /<owner>/<key>,
      // rendered a DIFFERENT, persisted org's chrome over the URL's owner).
      // A bounded retry costs nothing extra for the first case
      // (mwtOrgToReconcile keeps returning null, harmlessly, for the same
      // reason on every attempt) and closes the gap for the second — same
      // shared `_attempt` budget as every other wait in this function, not
      // an unbounded extra loop.
      if (_attempt < 25) {
        setTimeout(() => reconcileMwtOrgFromUrl(root, sdk, el, _attempt + 1, opts), 200)
        return
      }
      // TENANCY-URL-1 (2026-08-14, Nika: workspaces "fully isolated" — a
      // foreign URL must never label another tenant's render). Retries are
      // exhausted and the slug still resolves to NO member org: this session
      // cannot see that tenant. Every earlier cut fell through HERE and left
      // the ACTIVE workspace rendering under the foreign address
      // (/w/natali/ws_…/notes showing the bellforge module, measured live).
      // Rescope the address bar to the active org's canonical URL and
      // re-dispatch the workspace root — the foreign ws-handle and route
      // segments belong to the other tenant's namespace and must not be
      // reinterpreted inside this one. replaceState, not push: the content
      // was always the caller's own workspace; only the label lied.
      const _activeOrgRow =
        (activeForCompare &&
          root.orgs.find((o) => String(o.id ?? o._id ?? '') === String(activeForCompare))) ||
        root.org
      const _activeSlug = _activeOrgRow?.slug || _activeOrgRow?.username
      // CANVAS-PROJECT-PAGE-BOUNCE-1 (2026-08-21) — NOT-FOREIGN GUARD, and it
      // has to sit here, after the fallback above resolved `_activeSlug`.
      //
      // `_activeOrgRow` falls back to `root.org`, which a `/w/<slug>/…` landing
      // URL-PINS to the very slug this branch is about to judge foreign
      // (functions/auth.js). So when the two agree, the lookups above missed for
      // the OTHER reason this branch's own comment lists — `root.orgs` was
      // "non-empty enough" to pass the entry gate but had still not grown to
      // include the ACTIVE org inside the 25x200ms budget (or that row carries
      // no `slug`; `mwtOrgToReconcile` matches on `slug` only) — not because
      // another tenant is being rendered.
      //
      // Rescoping there rewrites org -> the same org and handle -> the same
      // handle, so its ONLY net effect is to DROP the route and SPA-navigate to
      // the workspace home:
      //     /w/nikoloza/ws_oT4pVpU0yT/p/word  ->  /w/nikoloza/ws_oT4pVpU0yT
      // which is the reported project-canvas bounce byte for byte (same org,
      // same workspace segment, project route gone, document alive because the
      // exit is `el.router('/')`, hence the title lagging behind the URL). The
      // ~18-20s delay is this ladder: the reconcile enters post-auth and the
      // budget expires ~5s later; the intermittency is whether the org row wins
      // that race. Compared case-insensitively because the URL segment is
      // user-typed/shared and org slugs are not case-significant.
      //
      // Tenant isolation is untouched: a slug that genuinely differs from the
      // active org's own slug still falls through and still rescopes
      // (TENANCY-URL-1) — see mwtRouterSelfRescope.test.js's last case.
      const _sameOrgSlug = (a, b) =>
        !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase()
      if (
        _sameOrgSlug(_activeSlug, slug) ||
        _sameOrgSlug(root.org?.slug, slug) ||
        _sameOrgSlug(root.org?.username, slug)
      ) {
        return
      }
      if (_activeSlug) {
        const _handle = shellResolveWorkspaceUrlHandle({
          workspace: root.workspace,
          org: _activeOrgRow,
          workspaces: Array.isArray(root.workspaces) ? root.workspaces : []
        })
        const _target = shellWithWorkspacePath(_activeSlug, _handle, '/')
        console.warn(
          '[mwt] URL org "' +
            slug +
            '" is outside this session\'s memberships — ' +
            'rescoped to ' +
            _target +
            ' (tenant isolation)'
        )
        try {
          win.history.replaceState(win.history.state, '', _target)
        } catch {}
        if (el && typeof el.router === 'function') {
          try {
            el.router('/', el.getRoot ? el.getRoot() : undefined)
          } catch {}
        }
      }
      return
    }
    console.log('[mwt] cross-org reconcile → switchOrg', slug, targetOrgId)
    Promise.resolve(sdk.switchOrg(targetOrgId, { skipPersist: true })).catch((err) =>
      console.warn('[mwt] cross-org reconcile failed:', err?.message || err)
    )
    return
  }

  // Bare URL (no `/w/` prefix) — canonicalize an INTRANET route to the active
  // org's `/w/<org-slug>` so every tab shows its workspace URL from load, not
  // just after navigation (design §8 test 8). replaceState (not push): the page
  // already rendered the correct route; we only rewrite the address bar, and the
  // AppShell history patch re-syncs `root.currentPath` (Phase-1 consumers strip).
  const pathOnly = path.split(/[?#]/)[0]
  if (!isMwtIntranetRoute(pathOnly, moduleRoutesFromRoot(root))) return
  // Resolve the active org from `root.org` — a bare load has no `sdkOrgId`, so
  // that is the authoritative source here; prefer the SDK-matched org only when
  // the SDK context is actually populated (a rare belt-and-suspenders path).
  const activeOrg =
    (sdkOrgId && root.orgs.find((o) => String(o.id ?? o._id ?? '') === String(sdkOrgId))) ||
    root.org
  const activeSlug = activeOrg?.slug || activeOrg?.username
  if (!activeSlug) return
  // Scheme A: canonicalize straight to the FULL form — org segment plus the
  // workspace segment when the active workspace needs one (shared rule), so a
  // bare `/tickets` load lands on `/w/acme/team-b/tickets` in one rewrite
  // instead of waiting for the next workspace-change sync to add the handle.
  const handle = shellResolveWorkspaceUrlHandle({
    workspace: root.workspace,
    org: activeOrg,
    workspaces: Array.isArray(root.workspaces) ? root.workspaces : []
  })
  const tail = (win.location.search || '') + (win.location.hash || '')
  const target = shellWithWorkspacePath(activeSlug, handle, path) + tail
  if (target === path + tail) return
  try {
    win.history.replaceState(win.history.state, '', target)
  } catch {}
}

// element.onRenderRouter — the initial hard-load render, which bypasses the
// wrapper (smbls calls the default router directly). Strip the address-bar path
// so a deep-linked /w/acme/tickets matches /tickets; the URL already shows the
// prefix on a hard load, so NEVER pushState. Mirrors smbls' onRouterRenderDefault
// 1:1 when MWT is off.
export const mwtOnRenderRouter = (el) => {
  const win = _mwtWin(el)
  const loc = win?.location
  if (!loc || !el?.routes) return
  const rawPath = loc.pathname || '/'
  const norm =
    rawPath === 'srcdoc' || rawPath === 'about:srcdoc' || rawPath === 'blank' ? '/' : rawPath
  // Always strip the reserved, unambiguous `/w/<slug>` prefix for route-matching —
  // a prefixed URL must resolve to its real route whether or not MWT is enabled. A
  // transient `?mwt=1` session (flag not persisted — dropped off the URL on the
  // first prefixed nav) leaves a `/w/<slug>` URL behind; loaded later with the flag
  // reading OFF, an un-stripped prefix fell through to the canvas
  // project-not-found 404 ("<slug> not found"). Mirrors the Phase-1 classifiers,
  // which all strip unconditionally (stripWorkspacePrefix is a no-op off-prefix).
  // WHITELABEL-STRICT-TENANT-SCOPE-1 — on a white-label host a URL naming
  // ANOTHER org (`/w/<other>/…`, `/studio/<other>`, another owner's canvas)
  // lands on the host's home on this FIRST render, which runs before any router
  // guard. Stripping a foreign `/w/keepz/default` used to leave `/default` — a
  // route that does not exist — so the tab showed "Not found".
  const foreignHome = whitelabelForeignRedirect(norm, el?.state?.root || el?.state)
  const matchPath = foreignHome || shellStripWorkspacePrefix(norm)
  // Flag OFF but the URL still carries a stale prefix → heal the address bar back to
  // the bare route so the tab isn't stranded on a dead `/w/<slug>` URL. When the flag
  // is ON the prefix is intentional (per-tab scope) and is preserved — except a
  // foreign one, which always leaves.
  // A foreign URL's query and hash belong to the other org's route — they are
  // not carried onto the host's home.
  const tail = foreignHome ? '' : (loc.search || '') + (loc.hash || '')
  if ((foreignHome || !shellIsMwtEnabled()) && matchPath !== norm && win.history) {
    try {
      win.history.replaceState(win.history.state, '', matchPath + tail)
    } catch {}
  }
  const url = matchPath + tail
  // Remember what this first dispatch asked for: the router answers it only
  // after its auth guard resolves, so the Scheme A heal (signin tick) can run
  // while this dispatch is still in flight and must not ask for it again.
  if (el.__ref) el.__ref.__mwtInitialPath = matchPath
  return defaultRouter(url, el, {}, { initialRender: true, pushState: false })
}
